import { useState, useEffect, useCallback, useMemo } from "react";
import {
  getFirestore,
  collection,
  onSnapshot,
  addDoc,
  updateDoc,
  deleteDoc,
  doc,
  Timestamp,
  query,
  orderBy,
  limit,
  startAfter,
  where,
  getDocs,
  getCountFromServer,
} from "firebase/firestore";
import { app as firebaseApp } from "../config/firebase";

const db = getFirestore(firebaseApp);

// Helper function to format Date objects or Firestore Timestamps to MM-DD-YYYY
const formatDate = (date) => {
  if (!date) return "";
  // Firestore Timestamp
  if (typeof date === "object" && date.seconds) {
    const d = new Date(date.seconds * 1000);
    const mm = String(d.getMonth() + 1).padStart(2, "0");
    const dd = String(d.getDate()).padStart(2, "0");
    const yyyy = d.getFullYear();
    return `${mm}-${dd}-${yyyy}`;
  }
  // JS Date
  if (date instanceof Date) {
    const mm = String(date.getMonth() + 1).padStart(2, "0");
    const dd = String(date.getDate()).padStart(2, "0");
    const yyyy = date.getFullYear();
    return `${mm}-${dd}-${yyyy}`;
  }
  // String
  if (typeof date === "string") {
    // Try to parse as date
    const d = new Date(date);
    if (!isNaN(d.getTime())) {
      const mm = String(d.getMonth() + 1).padStart(2, "0");
      const dd = String(d.getDate()).padStart(2, "0");
      const yyyy = d.getFullYear();
      return `${mm}-${dd}-${yyyy}`;
    }
    return date;
  }
  return date;
};

// Firestore requires that when a range/inequality operator (<, <=, >, >=,
// != ) is used in a where() clause, the first orderBy() must be on that same
// field. This detects such a constraint so callers can pass an arbitrary
// orderByField (e.g. "LastName") alongside a range filter on another field
// (e.g. "HSGY") without hitting a Firestore query error.
const RANGE_OPERATORS = ["<", "<=", ">", ">=", "!="];

const usePaginatedFirestore = (
  collectionName,
  pageSize = 1000,
  orderByField = null,
  orderByDirection = "asc",
  whereConstraints = []
) => {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [totalCount, setTotalCount] = useState(0);
  const [lastDoc, setLastDoc] = useState(null);
  const [hasNextPage, setHasNextPage] = useState(false);
  const [currentPage, setCurrentPage] = useState(0);
  const [cache, setCache] = useState(new Map());

  // Memoize the base collection reference
  const baseCollectionRef = useCallback(
    () => collection(db, collectionName),
    [collectionName]
  );

  const constraintsKey = JSON.stringify(whereConstraints || []);

  // constraintsKey is a content-based hash of whereConstraints; depending on
  // it (not the array reference) avoids re-running this on every render when
  // callers pass an inline array literal.
  const buildWhereClauses = useCallback(
    () =>
      (whereConstraints || []).map((c) => where(c.field, c.operator, c.value)),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [constraintsKey],
  );

  const rangeField = useMemo(() => {
    const rangeConstraint = (whereConstraints || []).find((c) =>
      RANGE_OPERATORS.includes(c.operator)
    );
    return rangeConstraint ? rangeConstraint.field : null;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [constraintsKey]);

  const buildOrderByClauses = useCallback(() => {
    const direction = orderByDirection === "desc" ? "desc" : "asc";
    if (rangeField && rangeField !== orderByField) {
      // The range-filtered field must be ordered first.
      const clauses = [orderBy(rangeField, "asc")];
      if (orderByField) clauses.push(orderBy(orderByField, direction));
      return clauses;
    }
    return orderByField ? [orderBy(orderByField, direction)] : [];
  }, [orderByField, orderByDirection, rangeField]);

  // Get total count (cached for performance)
  const getTotalCount = useCallback(async () => {
    try {
      const cacheKey = `${collectionName}_count_${constraintsKey}`;
      const cachedCount = cache.get(cacheKey);

      // Use cached count if it's less than 5 minutes old
      if (cachedCount && Date.now() - cachedCount.timestamp < 5 * 60 * 1000) {
        setTotalCount(cachedCount.count);
        return;
      }

      const countQueryRef = query(baseCollectionRef(), ...buildWhereClauses());
      const countSnapshot = await getCountFromServer(countQueryRef);
      const count = countSnapshot.data().count;

      // Cache the count
      setCache((prev) =>
        new Map(prev).set(cacheKey, {
          count,
          timestamp: Date.now(),
        })
      );

      setTotalCount(count);
    } catch (err) {
      console.error("Error getting total count:", err);
    }
  }, [collectionName, baseCollectionRef, cache, constraintsKey, buildWhereClauses]);

  // Fetch paginated data
  const fetchPage = useCallback(
    async (page = 0, startAfterDoc = null) => {
      setLoading(true);
      setError(null);

      try {
        // Build the query
        let queryRef = query(baseCollectionRef(), ...buildWhereClauses());

        queryRef = query(queryRef, ...buildOrderByClauses());

        queryRef = query(queryRef, limit(pageSize));

        if (startAfterDoc) {
          queryRef = query(queryRef, startAfter(startAfterDoc));
        }

        const querySnapshot = await getDocs(queryRef);
        const items = [];
        let lastDocument = null;

        querySnapshot.forEach((doc) => {
          const docData = doc.data();
          const processedData = {};

          // Process document data
          for (const key in docData) {
            if (docData.hasOwnProperty(key)) {
              const value = docData[key];
              // Keep created_at and updatedAt as Firestore Timestamp for DataGrid
              if (
                (key === "created_at" || key === "updatedAt") &&
                value instanceof Timestamp
              ) {
                processedData[key] = value;
              } else if (value instanceof Timestamp) {
                processedData[key] = formatDate(value.toDate());
              } else {
                processedData[key] = value;
              }
            }
          }

          items.push({ id: doc.id, ...processedData });
          lastDocument = doc;
        });

        setData(items);
        setLastDoc(lastDocument);
        setHasNextPage(querySnapshot.docs.length === pageSize);
        setCurrentPage(page);
        setLoading(false);
      } catch (err) {
        console.error(`Error fetching page from ${collectionName}:`, err);
        setError(
          `Failed to fetch data from ${collectionName}. Please try again later.`
        );
        setLoading(false);
      }
    },
    [
      collectionName,
      baseCollectionRef,
      buildWhereClauses,
      buildOrderByClauses,
      pageSize,
    ]
  );

  // Initialize: get total count and first page
  useEffect(() => {
    getTotalCount();
    fetchPage(0);
  }, [getTotalCount, fetchPage]);

  // Navigation functions
  const nextPage = useCallback(() => {
    if (hasNextPage && lastDoc) {
      fetchPage(currentPage + 1, lastDoc);
    }
  }, [hasNextPage, lastDoc, currentPage, fetchPage]);

  const prevPage = useCallback(() => {
    if (currentPage > 0) {
      // For previous page, we need to re-fetch from the beginning
      // This is a limitation of Firestore pagination
      fetchPage(0);
    }
  }, [currentPage, fetchPage]);

  const goToPage = useCallback(
    (page) => {
      if (page === 0) {
        fetchPage(0);
      } else {
        // For now, we'll just support next/prev navigation
        // To implement random page access, we'd need to cache page cursors
        console.warn(
          "Random page access not yet implemented. Use next/prev navigation."
        );
      }
    },
    [fetchPage]
  );

  // Add, update, delete functions
  const addDocument = useCallback(
    async (newItemData) => {
      const dataToAdd = {
        ...newItemData,
        created_at: newItemData.created_at || Timestamp.now(),
      };
      try {
        const docRef = await addDoc(baseCollectionRef(), dataToAdd);
        // Refresh current page to show new data
        fetchPage(currentPage, currentPage > 0 ? lastDoc : null);
        // Update total count
        getTotalCount();
        return docRef;
      } catch (err) {
        console.error(`Error adding document to ${collectionName}:`, err);
        throw err;
      }
    },
    [
      baseCollectionRef,
      collectionName,
      fetchPage,
      currentPage,
      lastDoc,
      getTotalCount,
    ]
  );

  const updateDocument = useCallback(
    async (docId, updateData) => {
      try {
        const docRef = doc(db, collectionName, docId);
        await updateDoc(docRef, updateData);
        // Refresh current page to show updated data
        fetchPage(currentPage, currentPage > 0 ? lastDoc : null);
        return docRef;
      } catch (err) {
        console.error(`Error updating document in ${collectionName}:`, err);
        throw err;
      }
    },
    [collectionName, fetchPage, currentPage, lastDoc]
  );

  const deleteDocument = useCallback(
    async (docId) => {
      try {
        const docRef = doc(db, collectionName, docId);
        await deleteDoc(docRef);
        // Refresh current page to show updated data
        fetchPage(currentPage, currentPage > 0 ? lastDoc : null);
        // Update total count
        getTotalCount();
        return docRef;
      } catch (err) {
        console.error(`Error deleting document from ${collectionName}:`, err);
        throw err;
      }
    },
    [collectionName, fetchPage, currentPage, lastDoc, getTotalCount]
  );

  return {
    data,
    loading,
    error,
    totalCount,
    currentPage,
    hasNextPage,
    hasPrevPage: currentPage > 0,
    nextPage,
    prevPage,
    goToPage,
    addDocument,
    updateDocument,
    deleteDocument,
    refresh: () => fetchPage(currentPage, currentPage > 0 ? lastDoc : null),
  };
};

export default usePaginatedFirestore;
